import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Header from '../../components/Header'
import MenuBar from '../../components/MenuBar'
import httpClient from '../../components/helpers/httpClient'
import PopupDropdown from '../../components/PopupDropdown'
import { DateField } from '@mui/x-date-pickers'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import { toast } from 'react-toastify'
import { showNotificationPopup } from '../../components/NotificationPopup'
import { parseDateValue, serializeDate } from '../../components/helpers/formatDate'
import { NumericFormat } from 'react-number-format'
import './TransactionDetailPage.css'
import DocumentsSection from '../../components/DocumentsSection'
import Button from '../../components/UI/Button'
import { formatPropertyIdentifier } from '../../components/helpers/propertyIdentifier'
import TransactionUnitTargets from '../../components/transactions/TransactionUnitTargets'
import OwnershipComposer from '../../components/transactions/OwnershipComposer'
import {
  createEmptyOwnershipPlan,
  hasOwnershipHolders,
  legacyParticipantsToOwnershipPlan,
  normalizeOwnershipPlan,
  ownershipPlanToLegacyParticipants,
  validateOwnershipPlan,
} from '../../components/transactions/ownershipPlan'

function TransactionDetailPage() {
  const navigate = useNavigate()
  const { transactionId } = useParams()

  const [loadingProperties, setLoadingProperties] = useState(false)
  const [properties, setProperties] = useState([])
  const [newTransactionObj, setNewTransactionObj] = useState({
    type: "",
    reservationDate: "",
    contractDate: "",
    deedDate: "",
    landPrice: "",
    constructionPrice: "",
    transactionPrice: "0",
    isAssetDeal: true,
    isShareDeal: false,
    transactedCompanyId: null,
    estimatedAssetValue: ""
  })
  const [selectedProperties, setSelectedProperties] = useState([])
  const [selectedUnits, setSelectedUnits] = useState([])
  const [contacts, setContacts] = useState([])
  const [companies, setCompanies] = useState([])
  const [selectedSellers, setSelectedSellers] = useState([])
  const [selectedBuyers, setSelectedBuyers] = useState([])
  const [sellerOwnershipPlan, setSellerOwnershipPlan] = useState(createEmptyOwnershipPlan)
  const [buyerOwnershipPlan, setBuyerOwnershipPlan] = useState(createEmptyOwnershipPlan)
  const [selectedNotaryBuyers, setSelectedNotaryBuyers] = useState([])
  const [selectedNotarySellers, setSelectedNotarySellers] = useState([])
  const [selectedBrokers, setSelectedBrokers] = useState([])
  const [selectedTransactedCompany, setSelectedTransactedCompany] = useState(null)
  const [menuItem, setMenuItem] = useState("properties")

  const BASISAKTE_TRANSACTION_TYPES = ["Basisakte", "Wijzigende basisakte"]

  // Check if current type should be treated as Basisakte to disable certain fields
  const isBasisakte = BASISAKTE_TRANSACTION_TYPES.includes(newTransactionObj.type)

  const fetchLatestBuyerForProperties = async (propertyIds) => {
    if (!propertyIds || propertyIds.length === 0) return null
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/transactions/latest-buyer?propertyIds=${propertyIds.join(',')}&structured=true`
      )
      if (response.status === 200 && response.data) {
        return response.data
      }
    } catch (error) {
      console.log("Error fetching latest buyer:", error)
    }
    return null
  }

  const fetchTransaction = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/transactions/get?transactionID=${transactionId}`)

    if (response.status == 200) {
      // Backend returns relationship entries like:
      // { id, type: "notary_buyer"|"notary_seller"|"broker", contact: {...}|null, company: {...}|null, ownership_* }
      // PopupDropdown renders contacts via first_name/last_name and companies via company_name,
      // so we flatten these entries into the same shape as our dropdown `options`.
      const flattenContactCompanyEntry = (entry) => {
        const ownership = {
          ownership_percentage: entry?.ownership_percentage,
          ownership_type: entry?.ownership_type,
          ownership_right_end_date: entry?.ownership_right_end_date
        }

        if (entry?.contact) {
          return {
            ...entry.contact,
            id: entry.contact.id,
            type: 'contact',
            ...ownership
          }
        }

        if (entry?.company) {
          return {
            ...entry.company,
            id: entry.company.id,
            type: 'company',
            ...ownership
          }
        }

        // Fallback (shouldn't happen, but avoids "Unknown Contact" with no id)
        return {
          id: entry?.id ?? null,
          type: 'contact',
          first_name: '',
          last_name: '',
          ...ownership
        }
      }

      const notaryBuyers = (response.data.notaryBuyers || []).map(flattenContactCompanyEntry)
      const notarySellers = (response.data.notarySellers || []).map(flattenContactCompanyEntry)
      const brokers = (response.data.brokers || []).map(flattenContactCompanyEntry)

      const formatSafeDate = (dateStr) => {
        return serializeDate(dateStr)
      }

      const reservationDate = formatSafeDate(response.data.reservation_date)
      const contractDate = formatSafeDate(response.data.contract_date)
      const deedDate = formatSafeDate(response.data.deed_date)

      setNewTransactionObj(prev => ({
        ...prev,
        transactionID: response.data.id,
        type: response.data.type,
        reservationDate: reservationDate,
        contractDate: contractDate,
        deedDate: deedDate,
        landPrice: response.data.land_price,
        constructionPrice: response.data.construction_price,
        transactionPrice: response.data.transaction_price,
        isAssetDeal: response.data.is_asset_deal !== undefined ? response.data.is_asset_deal : true,
        isShareDeal: response.data.is_share_deal !== undefined ? response.data.is_share_deal : false,
        transactedCompanyId: response.data.transactedCompany?.id || null,
        estimatedAssetValue: response.data.estimated_asset_value ?? ""
      }))

      const sellers = response.data.sellers.map(seller => ({
        ...seller,
        id: seller.contact?.id || seller.company?.id,
        first_name: seller.contact?.first_name || '',
        last_name: seller.contact?.last_name || '',
        company_name: seller.company?.company_name || '',
        type: seller.contact ? 'contact' : 'company',
        ownership_percentage: seller.ownership_percentage,
        ownership_type: seller.ownership_type,
        ownership_right_end_date: seller.ownership_right_end_date
      }))

      const buyers = response.data.buyers.map(buyer => ({
        ...buyer,
        id: buyer.contact?.id || buyer.company?.id,
        first_name: buyer.contact?.first_name || '',
        last_name: buyer.contact?.last_name || '',
        company_name: buyer.company?.company_name || '',
        type: buyer.contact ? 'contact' : 'company',
        ownership_percentage: buyer.ownership_percentage,
        ownership_type: buyer.ownership_type,
        ownership_right_end_date: buyer.ownership_right_end_date
      }))

      const loadedBuyerPlan = normalizeOwnershipPlan(
        hasOwnershipHolders(response.data.buyerOwnershipPlan)
          ? response.data.buyerOwnershipPlan
          : legacyParticipantsToOwnershipPlan(buyers),
      )
      const loadedSellerPlan = normalizeOwnershipPlan(
        hasOwnershipHolders(response.data.sellerOwnershipPlan)
          ? response.data.sellerOwnershipPlan
          : legacyParticipantsToOwnershipPlan(sellers),
      )
      setBuyerOwnershipPlan(loadedBuyerPlan)
      setSellerOwnershipPlan(loadedSellerPlan)
      setSelectedBuyers(ownershipPlanToLegacyParticipants(loadedBuyerPlan))
      setSelectedSellers(ownershipPlanToLegacyParticipants(loadedSellerPlan))
      setSelectedProperties((response.data.properties || []).map((property) => {
        const fallbackVat = Number(property.vat_percentage) || 0.21
        const fallbackReg = Number(property.registration_percentage) || 0.12
        return {
          ...property,
          land_tax_mode: property.land_tax_mode || 'registration',
          land_vat_percentage: Number(property.land_vat_percentage) || fallbackVat,
          land_registration_percentage: Number(property.land_registration_percentage) || fallbackReg,
          construction_tax_mode: property.construction_tax_mode || 'vat',
          construction_registration_percentage: Number(property.construction_registration_percentage) || fallbackReg,
          construction_vat_percentage: Number(property.construction_vat_percentage) || fallbackVat,
          connection_vat_percentage: Number(property.connection_vat_percentage) || fallbackVat,
          contract_vat_percentage: Number(property.contract_vat_percentage) || fallbackVat,
        }
      }))
      setSelectedUnits(response.data.unitTargets || [])
      setSelectedNotaryBuyers(notaryBuyers)
      setSelectedNotarySellers(notarySellers)
      setSelectedBrokers(brokers)
      if (response.data.transactedCompany) {
        setSelectedTransactedCompany({ id: response.data.transactedCompany.id, name: response.data.transactedCompany.company_name, type: 'company' })
      } else {
        setSelectedTransactedCompany(null)
      }
    }
    return response
  }

  const fetchProperties = async () => {
    setLoadingProperties(true)
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/properties/get?type=including-projects-units`)
    if (response.status == 200) {
      setProperties(response.data)
    }
    setLoadingProperties(false)
  }

  const fetchContacts = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/contacts/get`)
    if (response.status == 200) {
      setContacts(response.data)
    }
  }

  const fetchCompanies = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/companies/get?type=limited-info`)
    if (response.status == 200) {
      setCompanies(response.data)
    }
  }

  useEffect(() => {
    fetchProperties()
    fetchContacts()
    fetchCompanies()
  }, [])

  useEffect(() => {
    setNewTransactionObj(prev => ({
      ...prev,
      sellers: selectedSellers,
      buyers: selectedBuyers,
      properties: selectedProperties,
      units: selectedUnits,
      notaryBuyers: selectedNotaryBuyers,
      notarySellers: selectedNotarySellers,
      brokers: selectedBrokers,
      transactedCompanyId: selectedTransactedCompany?.id || null
    }))
  }, [selectedBuyers, selectedProperties, selectedUnits, selectedSellers, selectedNotaryBuyers, selectedNotarySellers, selectedBrokers, selectedTransactedCompany])

  const handleSellerOwnershipPlanChange = nextPlan => {
    setSellerOwnershipPlan(nextPlan)
    setSelectedSellers(ownershipPlanToLegacyParticipants(nextPlan))
  }

  const handleBuyerOwnershipPlanChange = nextPlan => {
    setBuyerOwnershipPlan(nextPlan)
    setSelectedBuyers(ownershipPlanToLegacyParticipants(nextPlan))
  }

  useEffect(() => {
    fetchTransaction().catch(error => {
      if (error.response?.status === 404) {
        console.log("Transaction not found")
      } else {
        console.log("Error fetching transaction:", error)
      }
    })
  }, [transactionId])

  const handleSetStateNewTransactionType = async (value) => {
    setNewTransactionObj({ ...newTransactionObj, type: value })
    
    // If Basisakte is selected, auto-populate buyer and seller with the current owners
    if (BASISAKTE_TRANSACTION_TYPES.includes(value) && selectedProperties.length > 0) {
      const propertyIds = selectedProperties.map(p => p.id)
      const latestOwnership = await fetchLatestBuyerForProperties(propertyIds)
      const latestBuyers = Array.isArray(latestOwnership) ? latestOwnership : latestOwnership?.buyers
      
      if (latestBuyers && latestBuyers.length > 0) {
        const ownerEntries = latestBuyers.map(buyer => ({
          id: buyer.id,
          first_name: buyer.first_name || '',
          last_name: buyer.last_name || '',
          company_name: buyer.company_name || '',
          type: buyer.type,
          ownership_percentage: buyer.ownership_percentage || "100",
          ownership_type: buyer.ownership_type || "volle eigendom",
          ownership_right_end_date: buyer.ownership_right_end_date || null
        }))
        const latestPlan = normalizeOwnershipPlan(
          (!Array.isArray(latestOwnership) && hasOwnershipHolders(latestOwnership.buyerOwnershipPlan))
            ? latestOwnership.buyerOwnershipPlan
            : legacyParticipantsToOwnershipPlan(ownerEntries),
        )
        handleSellerOwnershipPlanChange(normalizeOwnershipPlan(latestPlan))
        handleBuyerOwnershipPlanChange(normalizeOwnershipPlan(latestPlan))
      }
    }
  }

  const handleSetStateNewTransactionObj = (field, value) => {
    setNewTransactionObj({ ...newTransactionObj, [field]: value })
  }

  const handleSave = async () => {
    const requiredFields = {
      type: "Transaction type",
      sellers: "Sellers",
      buyers: "Buyers",
    }

    if (selectedProperties.length === 0 && selectedUnits.length === 0) {
      toast.error('At least one property or unit is required')
      return
    }

    const planChecks = [
      ['Seller ownership', validateOwnershipPlan(sellerOwnershipPlan)],
      ['Buyer ownership', validateOwnershipPlan(buyerOwnershipPlan)],
    ]
    const invalidPlan = planChecks.find(([, result]) => !result.valid)
    if (invalidPlan) {
      toast.error(`${invalidPlan[0]}: ${invalidPlan[1].errors[0]}`)
      return
    }

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!newTransactionObj[field] || (Array.isArray(newTransactionObj[field]) && newTransactionObj[field].length === 0)) {
        toast.error(`${label} is required`)
        return
      }
    }

    const propertiesWithPrices = newTransactionObj.properties.map(property => ({
      id: property.id,
      land_price: property.land_price || 0,
      land_tax_mode: property.land_tax_mode || 'registration',
      land_vat_percentage: property.land_vat_percentage ?? property.vat_percentage ?? 0.21,
      land_registration_percentage: property.land_registration_percentage ?? property.registration_percentage ?? 0.12,
      construction_price: property.construction_price || 0,
      construction_tax_mode: property.construction_tax_mode || 'vat',
      connection_cost: property.connection_cost || 0,
      contract_cost: property.contract_cost || 0,
      vat_percentage: property.vat_percentage || 0.21,
      construction_vat_percentage: property.construction_vat_percentage ?? property.vat_percentage ?? 0.21,
      connection_vat_percentage: property.connection_vat_percentage ?? property.vat_percentage ?? 0.21,
      contract_vat_percentage: property.contract_vat_percentage ?? property.vat_percentage ?? 0.21,
      construction_registration_percentage: property.construction_registration_percentage ?? property.registration_percentage ?? 0.12,
      registration_percentage: property.registration_percentage || 0.12
    }))

    const unitsWithPrices = selectedUnits.map(unit => ({
      id: unit.id,
      land_price: unit.land_price || 0,
      land_tax_mode: unit.land_tax_mode || 'registration',
      land_vat_percentage: unit.land_vat_percentage ?? unit.vat_percentage ?? 0.21,
      land_registration_percentage: unit.land_registration_percentage ?? unit.registration_percentage ?? 0.12,
      construction_price: unit.construction_price || 0,
      construction_tax_mode: unit.construction_tax_mode || 'vat',
      connection_cost: unit.connection_cost || 0,
      contract_cost: unit.contract_cost || 0,
      vat_percentage: unit.vat_percentage || 0.21,
      construction_vat_percentage: unit.construction_vat_percentage ?? unit.vat_percentage ?? 0.21,
      connection_vat_percentage: unit.connection_vat_percentage ?? unit.vat_percentage ?? 0.21,
      contract_vat_percentage: unit.contract_vat_percentage ?? unit.vat_percentage ?? 0.21,
      construction_registration_percentage: unit.construction_registration_percentage ?? unit.registration_percentage ?? 0.12,
      registration_percentage: unit.registration_percentage || 0.12,
    }))

    const transactionData = {
      ...newTransactionObj,
      properties: propertiesWithPrices,
      units: unitsWithPrices,
      reservationDate: serializeDate(newTransactionObj.reservationDate),
      contractDate: serializeDate(newTransactionObj.contractDate),
      deedDate: serializeDate(newTransactionObj.deedDate),
      notaryBuyers: newTransactionObj.notaryBuyers || [],
      notarySellers: newTransactionObj.notarySellers || [],
      brokers: newTransactionObj.brokers || [],
      isAssetDeal: Boolean(newTransactionObj.isAssetDeal),
      isShareDeal: Boolean(newTransactionObj.isShareDeal),
      transactedCompanyId: newTransactionObj.isShareDeal ? (selectedTransactedCompany?.id || null) : null,
      estimatedAssetValue: newTransactionObj.isShareDeal ? (newTransactionObj.estimatedAssetValue ?? null) : null,
      sellerOwnershipPlan,
      buyerOwnershipPlan,
    }

    try {
      const response = await httpClient.put(import.meta.env.VITE_API_URL + `/api/transactions/update`, transactionData)
      if (response.status == 200) {
        toast.success("Transaction updated successfully")
        navigate('/transactions')
      } else {
        toast.error("Error updating transaction")
      }
    } catch (e) {
      toast.error(e.response?.data?.errors?.[0] || e.response?.data?.message || "Error updating transaction")
    }
  }

  const handleDelete = () => {
    showNotificationPopup({
      message: "Are you sure you want to delete this transaction?",
      onAction: confirmDelete,
      actionButton: { text: "Delete", color: "var(--red)" }
    })
  }

  const confirmDelete = async () => {
    const response = await httpClient.delete(import.meta.env.VITE_API_URL + `/api/transactions/delete?transactionID=${transactionId}`)
    if (response.status === 200) {
      toast.success("Transaction deleted successfully")
      navigate('/transactions')
    } else {
      toast.error("Error deleting transaction")
    }
  }

  const handleSelectProperty = (property) => {
    if (selectedProperties.some(p => p.id === property.id)) return
    const commercialPrice = property.property_x_unit_set[0]?.unit_id?.commercial_price || {}
    const fallbackVat = Number(commercialPrice.vat_percentage) || 0.21
    const fallbackReg = Number(commercialPrice.registration_percentage) || 0.12
    const propertyWithPrices = {
      ...property,
      land_price: Number(commercialPrice.land_price) || 0,
      land_tax_mode: commercialPrice.land_tax_mode || 'registration',
      land_vat_percentage: Number(commercialPrice.land_vat_percentage) || fallbackVat,
      land_registration_percentage: Number(commercialPrice.land_registration_percentage) || fallbackReg,
      construction_price: Number(commercialPrice.construction_price) || 0,
      construction_tax_mode: commercialPrice.construction_tax_mode || 'vat',
      connection_cost: Number(commercialPrice.connection_cost) || 0,
      contract_cost: Number(commercialPrice.contract_cost) || 0,
      vat_percentage: fallbackVat,
      construction_vat_percentage: Number(commercialPrice.construction_vat_percentage) || fallbackVat,
      connection_vat_percentage: Number(commercialPrice.connection_vat_percentage) || fallbackVat,
      contract_vat_percentage: Number(commercialPrice.contract_vat_percentage) || fallbackVat,
      construction_registration_percentage: Number(commercialPrice.construction_registration_percentage) || fallbackReg,
      registration_percentage: Number(commercialPrice.registration_percentage) || 0.12
    }
    setSelectedProperties([...selectedProperties, propertyWithPrices])
  }

  const handleRemoveProperty = (property) => {
    setSelectedProperties(selectedProperties.filter(p => p.id !== property.id))
  }

  const handleSetStateNewTransactionNotaryBuyers = (values) => { setSelectedNotaryBuyers(values) }
  const handleSetStateNewTransactionNotarySellers = (values) => { setSelectedNotarySellers(values) }
  const handleSetStateNewTransactionBrokers = (values) => { setSelectedBrokers(values) }

  const handlePropertyPriceUpdate = (propertyId, field, value) => {
    setSelectedProperties(prevProperties => prevProperties.map(property => property.id === propertyId ? { ...property, [field]: value } : property))
    const updatedProperty = selectedProperties.find(p => p.id === propertyId)
    if (updatedProperty) {
      const updatedProperties = selectedProperties.map(p => p.id === propertyId ? { ...p, [field]: value } : p)
      setNewTransactionObj(prev => ({ ...prev, properties: updatedProperties }))
    }
  }

  const calculateTotalTransactionPrice = () => {
    const directUnitIds = new Set(selectedUnits.map(unit => unit.id))
    const propertiesWithoutDirectUnit = selectedProperties.filter(property => (
      !property.property_x_unit_set?.some(link => directUnitIds.has(link.unit_id?.id))
    ))

    return [...propertiesWithoutDirectUnit, ...selectedUnits].reduce((accumulator, target) => {
      const landPrice = Number(target.land_price) || 0
      const constructionPrice = Number(target.construction_price) || 0
      const connectionCost = Number(target.connection_cost) || 0
      const contractCost = Number(target.contract_cost) || 0
      const subtotalForVat = constructionPrice + connectionCost + contractCost
      const subtotal = landPrice + subtotalForVat
      return accumulator + subtotal
    }, 0)
  }

  useEffect(() => {
    const totalPrice = calculateTotalTransactionPrice()
    const propertiesWithExplicitPrices = selectedProperties.map(property => ({
      id: property.id,
      land_price: property.land_price || 0,
      land_tax_mode: property.land_tax_mode || 'registration',
      land_vat_percentage: property.land_vat_percentage ?? property.vat_percentage ?? 0.21,
      land_registration_percentage: property.land_registration_percentage ?? property.registration_percentage ?? 0.12,
      construction_price: property.construction_price || 0,
      construction_tax_mode: property.construction_tax_mode || 'vat',
      connection_cost: property.connection_cost || 0,
      contract_cost: property.contract_cost || 0,
      vat_percentage: property.vat_percentage || 0.21,
      construction_vat_percentage: property.construction_vat_percentage ?? property.vat_percentage ?? 0.21,
      connection_vat_percentage: property.connection_vat_percentage ?? property.vat_percentage ?? 0.21,
      contract_vat_percentage: property.contract_vat_percentage ?? property.vat_percentage ?? 0.21,
      construction_registration_percentage: property.construction_registration_percentage ?? property.registration_percentage ?? 0.12,
      registration_percentage: property.registration_percentage || 0.12,
      cadastral_nr: property.cadastral_nr,
      partition_nr: property.partition_nr,
      location: property.location,
      function: property.function,
      property_x_unit_set: property.property_x_unit_set
    }))
    setNewTransactionObj(prev => ({ ...prev, transactionPrice: totalPrice, properties: propertiesWithExplicitPrices }))
  }, [selectedProperties, selectedUnits])

  const getTotalsForProperty = (property) => {
    const landPrice = Number(property.land_price) || 0
    const constructionPrice = Number(property.construction_price) || 0
    const connectionCost = Number(property.connection_cost) || 0
    const contractCost = Number(property.contract_cost) || 0
    const fallbackVat = Number(property.vat_percentage) || 0.21
    const fallbackReg = Number(property.registration_percentage) || 0.12
    const landTaxMode = property.land_tax_mode || 'registration'
    const constructionTaxMode = property.construction_tax_mode || 'vat'
    const landVatPercentage = Number(property.land_vat_percentage ?? fallbackVat) || 0
    const landRegistrationPercentage = Number(property.land_registration_percentage ?? fallbackReg) || 0
    const constructionVatPercentage = Number(property.construction_vat_percentage ?? fallbackVat) || 0
    const constructionRegistrationPercentage = Number(property.construction_registration_percentage ?? fallbackReg) || 0
    const connectionVatPercentage = Number(property.connection_vat_percentage ?? fallbackVat) || 0
    const contractVatPercentage = Number(property.contract_vat_percentage ?? fallbackVat) || 0
    const landTax = landTaxMode === 'vat'
      ? landPrice * landVatPercentage
      : landPrice * landRegistrationPercentage
    const constructionTax = constructionTaxMode === 'vat'
      ? constructionPrice * constructionVatPercentage
      : constructionPrice * constructionRegistrationPercentage
    const subtotalForVat = constructionPrice + connectionCost + contractCost
    const vatAmount =
      (landTaxMode === 'vat' ? landTax : 0) +
      (constructionTaxMode === 'vat' ? constructionTax : 0) +
      (connectionCost * connectionVatPercentage) +
      (contractCost * contractVatPercentage)
    const registrationAmount =
      (landTaxMode === 'registration' ? landTax : 0) +
      (constructionTaxMode === 'registration' ? constructionTax : 0)
    const subtotal = landPrice + subtotalForVat
    const total = subtotal + vatAmount + registrationAmount
    return { subtotal, vatAmount, registrationAmount, total }
  }

  return (
    <div className='template'>
      <Header>
        <div className='header__left'>
          <div className="back_button" onClick={() => navigate(-1)}><KeyboardBackspaceIcon /> Go Back</div>
        </div>
        <div className='header__options--right'>
          <Button variant='secondary' tone='red' size='sm' onClick={handleDelete}>Delete</Button>
          <Button variant='primary' size='sm' onClick={handleSave}>Save</Button>
        </div>
      </Header>
      <MenuBar />
      <div className='template__body' id='transaction-detail'>
        <div className='menu__titles'>
          <h3 className={`${menuItem == "properties" ? "selected" : ""}`} onClick={() => setMenuItem("properties")}>units & properties</h3>
          <h3 className={`${menuItem == "details" ? "selected" : ""}`} onClick={() => setMenuItem("details")}>details</h3>
          <h3 className={`${menuItem == "documents" ? "selected" : ""}`} onClick={() => setMenuItem("documents")}>documents</h3>
        </div>
        <div className={`body__section ${menuItem}`}>
          {menuItem === 'properties' && (
            <div className='panel'>
              <div className='selected_properties_list'>
                <TransactionUnitTargets
                  selectedUnits={selectedUnits}
                  onChange={setSelectedUnits}
                  isBasisakte={isBasisakte}
                  properties={properties}
                  selectedProperties={selectedProperties}
                  loadingProperties={loadingProperties}
                  onSelectProperty={handleSelectProperty}
                />
                <div className='properties_list'>
                  {selectedProperties.map((property, index) => {
                    const unitNames = property.property_x_unit_set.map(pxu => pxu.unit_id.unit_name).join(', ')
                    const projectNames = property.property_x_unit_set
                      .map(pxu => pxu.unit_id.building_id.project_id.project_placeholder_name)
                      .filter((value, index, self) => self.indexOf(value) === index)
                      .join(', ')

                    const totals = getTotalsForProperty(property)

                    return (
                      <div key={index}>
                        <div className='selected_property_item' onClick={() => handleRemoveProperty(property)}>
                          <div className='property_item_value transaction-target-identity'>
                            <span className='transaction-target-badge property'>Property</span>
                            <span>{formatPropertyIdentifier(property.cadastral_nr, property.partition_nr)}</span>
                          </div>
                          <div className='property_item_value'>{property.location}</div>
                          <div className='property_item_value'>{property.function}</div>
                          <div className='property_item_value'>{unitNames}</div>
                          <div className='property_item_value'>{projectNames}</div>
                          <div className='property_item__icon min'>-</div>
                        </div>

                        {!isBasisakte && (
                          <div className='property-price-details'>
                            <div className="price-inputs">
                              <div className="input_field--combo-grid">
                                <label className="input_field__label">Land</label>
                                <div className="input-with-euro">
                                  <span className="euro-sign">€</span>
                                  <NumericFormat
                                    className="input_field__input_box--text"
                                    value={property.land_price}
                                    thousandSeparator=" "
                                    decimalSeparator="," 
                                    decimalScale={2}
                                    allowNegative={false}
                                    onValueChange={(values) => {
                                      const { floatValue } = values
                                      handlePropertyPriceUpdate(property.id, 'land_price', floatValue ?? '')
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="input_field--combo-grid small">
                                <label className="input_field__label">Land Tax Mode</label>
                                <select
                                  className="input_field__input_box--text"
                                  value={property.land_tax_mode || 'registration'}
                                  onChange={(e) => handlePropertyPriceUpdate(property.id, 'land_tax_mode', e.target.value)}
                                >
                                  <option value="registration">Registration</option>
                                  <option value="vat">VAT</option>
                                </select>
                              </div>

                              <div className="input_field--combo-grid small">
                                <label className="input_field__label">{(property.land_tax_mode || 'registration') === 'vat' ? 'Land VAT' : 'Land Registration'}</label>
                                <div className="input-with-euro">
                                  <NumericFormat
                                    className="input_field__input_box--text"
                                    value={((property.land_tax_mode || 'registration') === 'vat'
                                      ? (property.land_vat_percentage ?? property.vat_percentage ?? 0.21)
                                      : (property.land_registration_percentage ?? property.registration_percentage ?? 0.12)) * 100}
                                    thousandSeparator=" "
                                    decimalSeparator="," 
                                    decimalScale={2}
                                    allowNegative={false}
                                    onValueChange={(values) => {
                                      const { floatValue } = values
                                      const nextValue = floatValue !== undefined ? floatValue / 100 : ''
                                      if ((property.land_tax_mode || 'registration') === 'vat') {
                                        handlePropertyPriceUpdate(property.id, 'land_vat_percentage', nextValue)
                                      } else {
                                        handlePropertyPriceUpdate(property.id, 'land_registration_percentage', nextValue)
                                      }
                                    }}
                                  />
                                  <span className="euro-sign">%</span>
                                </div>
                              </div>

                              <div className="input_field--combo-grid">
                                <label className="input_field__label">Construction</label>
                                <div className="input-with-euro">
                                  <span className="euro-sign">€</span>
                                  <NumericFormat
                                    className="input_field__input_box--text"
                                    value={property.construction_price}
                                    thousandSeparator=" "
                                    decimalSeparator="," 
                                    decimalScale={2}
                                    allowNegative={false}
                                    onValueChange={(values) => {
                                      const { floatValue } = values
                                      handlePropertyPriceUpdate(property.id, 'construction_price', floatValue ?? '')
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="input_field--combo-grid small">
                                <label className="input_field__label">Construction Tax Mode</label>
                                <select
                                  className="input_field__input_box--text"
                                  value={property.construction_tax_mode || 'vat'}
                                  onChange={(e) => handlePropertyPriceUpdate(property.id, 'construction_tax_mode', e.target.value)}
                                >
                                  <option value="vat">VAT</option>
                                  <option value="registration">Registration</option>
                                </select>
                              </div>

                              <div className="input_field--combo-grid small">
                                <label className="input_field__label">{(property.construction_tax_mode || 'vat') === 'vat' ? 'Construction VAT' : 'Construction Registration'}</label>
                                <div className="input-with-euro">
                                  <NumericFormat
                                    className="input_field__input_box--text"
                                    value={((property.construction_tax_mode || 'vat') === 'vat'
                                      ? (property.construction_vat_percentage ?? property.vat_percentage ?? 0.21)
                                      : (property.construction_registration_percentage ?? property.registration_percentage ?? 0.12)) * 100}
                                    thousandSeparator=" "
                                    decimalSeparator="," 
                                    decimalScale={2}
                                    allowNegative={false}
                                    onValueChange={(values) => {
                                      const { floatValue } = values
                                      const nextValue = floatValue !== undefined ? floatValue / 100 : ''
                                      if ((property.construction_tax_mode || 'vat') === 'vat') {
                                        handlePropertyPriceUpdate(property.id, 'construction_vat_percentage', nextValue)
                                        handlePropertyPriceUpdate(property.id, 'vat_percentage', nextValue)
                                      } else {
                                        handlePropertyPriceUpdate(property.id, 'construction_registration_percentage', nextValue)
                                      }
                                    }}
                                  />
                                  <span className="euro-sign">%</span>
                                </div>
                              </div>

                              <div className="input_field--combo-grid">
                                <label className="input_field__label">Connection</label>
                                <div className="input-with-euro">
                                  <span className="euro-sign">€</span>
                                  <NumericFormat
                                    className="input_field__input_box--text"
                                    value={property.connection_cost}
                                    thousandSeparator=" "
                                    decimalSeparator="," 
                                    decimalScale={2}
                                    allowNegative={false}
                                    onValueChange={(values) => {
                                      const { floatValue } = values
                                      handlePropertyPriceUpdate(property.id, 'connection_cost', floatValue ?? '')
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="input_field--combo-grid">
                                <label className="input_field__label">Contract</label>
                                <div className="input-with-euro">
                                  <span className="euro-sign">€</span>
                                  <NumericFormat
                                    className="input_field__input_box--text"
                                    value={property.contract_cost}
                                    thousandSeparator=" "
                                    decimalSeparator="," 
                                    decimalScale={2}
                                    allowNegative={false}
                                    onValueChange={(values) => {
                                      const { floatValue } = values
                                      handlePropertyPriceUpdate(property.id, 'contract_cost', floatValue ?? '')
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="input_field--combo-grid small">
                                <label className="input_field__label">VAT Utility</label>
                                <div className="input-with-euro">
                                  <NumericFormat
                                    className="input_field__input_box--text"
                                    value={property.connection_vat_percentage !== '' && property.connection_vat_percentage !== undefined && property.connection_vat_percentage !== null ? property.connection_vat_percentage * 100 : 21}
                                    thousandSeparator=" "
                                    decimalSeparator="," 
                                    decimalScale={2}
                                    allowNegative={false}
                                    onValueChange={(values) => {
                                      const { floatValue } = values
                                      handlePropertyPriceUpdate(property.id, 'connection_vat_percentage', floatValue !== undefined ? floatValue / 100 : '')
                                    }}
                                  />
                                  <span className="euro-sign">%</span>
                                </div>
                              </div>

                              <div className="input_field--combo-grid small">
                                <label className="input_field__label">VAT Contract</label>
                                <div className="input-with-euro">
                                  <NumericFormat
                                    className="input_field__input_box--text"
                                    value={property.contract_vat_percentage !== '' && property.contract_vat_percentage !== undefined && property.contract_vat_percentage !== null ? property.contract_vat_percentage * 100 : 21}
                                    thousandSeparator=" "
                                    decimalSeparator="," 
                                    decimalScale={2}
                                    allowNegative={false}
                                    onValueChange={(values) => {
                                      const { floatValue } = values
                                      handlePropertyPriceUpdate(property.id, 'contract_vat_percentage', floatValue !== undefined ? floatValue / 100 : '')
                                    }}
                                  />
                                  <span className="euro-sign">%</span>
                                </div>
                              </div>
                            </div>

                            <div className="total-row">
                              <div>Subtotal: €<NumericFormat value={totals.subtotal} thousandSeparator=" " decimalSeparator="," decimalScale={2} fixedDecimalScale={true} displayType="text" /></div>
                              <div>VAT: €<NumericFormat value={totals.vatAmount} thousandSeparator=" " decimalSeparator="," decimalScale={2} fixedDecimalScale={true} displayType="text" /></div>
                              <div>Registration: €<NumericFormat value={totals.registrationAmount} thousandSeparator=" " decimalSeparator="," decimalScale={2} fixedDecimalScale={true} displayType="text" /></div>
                              <div>Total (excl. VAT & Registration): €<NumericFormat value={totals.subtotal} thousandSeparator=" " decimalSeparator="," decimalScale={2} fixedDecimalScale={true} displayType="text" /></div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}

                </div>
              </div>
            </div>
          )}

          {menuItem === 'details' && (
            <>
              <div className='panel overflow-y-auto'>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="transaction_type">Type</label>
                  <PopupDropdown
                    options={["Oorsprong", "Aankoop", "Verkoop", "Basisakte", "Wijzigende basisakte"]}
                    value={newTransactionObj.type}
                    onChange={handleSetStateNewTransactionType}
                    title={"Select type"}
                    searchBar={false}
                  />
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="reservation_date">Reservation date</label>
                  <DateField
                    className="date-picker"
                    format="DD-MM-YYYY"
                    value={parseDateValue(newTransactionObj.reservationDate)}
                    onChange={(value) => {
                      const formattedDate = serializeDate(value)
                      handleSetStateNewTransactionObj("reservationDate", formattedDate)
                    }}
                    disabled={isBasisakte}
                  />
                </div>
                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="contract_date">Contract date</label>
                  <DateField
                    className="date-picker"
                    format="DD-MM-YYYY"
                    value={parseDateValue(newTransactionObj.contractDate)}
                    onChange={(value) => {
                      const formattedDate = serializeDate(value)
                      handleSetStateNewTransactionObj("contractDate", formattedDate)
                    }}
                    disabled={isBasisakte}
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="deed_date">Deed date</label>
                  <DateField
                    className="date-picker"
                    format="DD-MM-YYYY"
                    value={parseDateValue(newTransactionObj.deedDate)}
                    onChange={(value) => {
                      const formattedDate = serializeDate(value)
                      handleSetStateNewTransactionObj("deedDate", formattedDate)
                    }}
                  />
                </div>
                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="transaction_price">Total Price</label>
                  <div className="input-with-euro">
                    <span className="euro-sign">€</span>
                    <NumericFormat
                      className="input_field__input_box--text price"
                      id="transaction_price"
                      name="transaction_price"
                      value={newTransactionObj.transactionPrice}
                      thousandSeparator=" "
                      decimalSeparator="," 
                      decimalScale={2}
                      fixedDecimalScale={true}
                      displayType="input"
                      readOnly
                    />
                  </div>
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="notary_buyer">Notary Buyer</label>
                  <PopupDropdown
                    searchBar={true}
                    options={[...contacts.map(item => ({ ...item, type: 'contact' })), ...companies.map(item => ({ ...item, type: 'company' }))]}
                    value={selectedNotaryBuyers}
                    onChange={handleSetStateNewTransactionNotaryBuyers}
                    title={"Select notary buyers"}
                    multiselect={true}
                    disabled={isBasisakte}
                  />
                </div>

                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="notary_seller">Notary Seller</label>
                  <PopupDropdown
                    searchBar={true}
                    options={[...contacts.map(item => ({ ...item, type: 'contact' })), ...companies.map(item => ({ ...item, type: 'company' }))]}
                    value={selectedNotarySellers}
                    onChange={handleSetStateNewTransactionNotarySellers}
                    title={"Select notary sellers"}
                    multiselect={true}
                  />
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label" htmlFor="broker">Broker</label>
                  <PopupDropdown
                    searchBar={true}
                    options={[...contacts.map(item => ({ ...item, type: 'contact' })), ...companies.map(item => ({ ...item, type: 'company' }))]}
                    value={selectedBrokers}
                    onChange={handleSetStateNewTransactionBrokers}
                    title={"Select brokers"}
                    multiselect={true}
                    disabled={isBasisakte}
                  />
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label">Deal Type</label>
                  <PopupDropdown
                    options={[{ id: 'asset', name: 'Asset deal' }, { id: 'share', name: 'Share deal' }]}
                    value={newTransactionObj.isShareDeal ? { id: 'share', name: 'Share deal' } : { id: 'asset', name: 'Asset deal' }}
                    onChange={(opt) => {
                      const isShare = opt?.id === 'share'
                      setNewTransactionObj(prev => ({
                        ...prev,
                        isShareDeal: isShare,
                        isAssetDeal: !isShare,
                        estimatedAssetValue: isShare ? prev.estimatedAssetValue : ""
                      }))
                      if (!isShare) {
                        setSelectedTransactedCompany(null)
                      }
                    }}
                    title={"Select deal type"}
                    searchBar={false}
                    disabled={isBasisakte}
                  />
                </div>

                <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                  <label className="input_field__label">Transacted company</label>
                  <PopupDropdown
                    searchBar={true}
                    options={[...companies.map(item => ({ id: item.id, name: item.company_name, type: 'company' }))]}
                    value={selectedTransactedCompany}
                    onChange={(value) => {
                      setSelectedTransactedCompany(value)
                    }}
                    title={"Select company"}
                    multiselect={false}
                    disabled={isBasisakte || !newTransactionObj.isShareDeal}
                  />
                </div>

                {newTransactionObj.isShareDeal && (
                  <>
                    <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                      <label className="input_field__label" htmlFor="estimated_asset_value">Estimated Asset Value</label>
                      <div className="input-with-euro">
                        <span className="euro-sign">€</span>
                        <NumericFormat
                          className="input_field__input_box--text price"
                          id="estimated_asset_value"
                          name="estimated_asset_value"
                          value={newTransactionObj.estimatedAssetValue}
                          thousandSeparator=" "
                          decimalSeparator=","
                          decimalScale={2}
                          fixedDecimalScale={true}
                          allowNegative={false}
                          displayType="input"
                          disabled={isBasisakte}
                          onValueChange={(values) => {
                            handleSetStateNewTransactionObj("estimatedAssetValue", values.floatValue ?? "")
                          }}
                        />
                      </div>
                    </div>

                    <div className={`input_field--combo-grid ${isBasisakte ? 'disabled' : ''}`}>
                      <label className="input_field__label" htmlFor="latent_buyer_gain">Latente meerwaarde koper</label>
                      <div className="input-with-euro">
                        <span className="euro-sign">€</span>
                        <NumericFormat
                          className="input_field__input_box--text price"
                          id="latent_buyer_gain"
                          name="latent_buyer_gain"
                          value={(Number(newTransactionObj.estimatedAssetValue) || 0) - (Number(newTransactionObj.transactionPrice) || 0)}
                          thousandSeparator=" "
                          decimalSeparator=","
                          decimalScale={2}
                          fixedDecimalScale={true}
                          displayType="input"
                          readOnly
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className='panel'>
                <div className='ownership-composers-stack'>
                  <OwnershipComposer
                    title='Sellers'
                    plan={sellerOwnershipPlan}
                    onChange={handleSellerOwnershipPlanChange}
                    contacts={contacts}
                    companies={companies}
                    calculatedShareLabel='Calculated proceeds share'
                  />
                  <OwnershipComposer
                    title='Buyers'
                    plan={buyerOwnershipPlan}
                    onChange={handleBuyerOwnershipPlanChange}
                    contacts={contacts}
                    companies={companies}
                    calculatedShareLabel='Calculated invoice share'
                  />
                </div>
              </div>
            </>
          )}

          {menuItem === 'documents' && (
            <div className='panel overflow-y-auto'>
              <DocumentsSection scope="transaction" entityId={transactionId} editable={true} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TransactionDetailPage
